import { EventEmitter, Injectable, signal } from '@angular/core';

import { SlipState } from '@bpos/v1/my-bets';
import { CrmConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { MyBetsListSlimResponse } from '@frontend/sports/types/models/my-bets';
import { LocalStoreService } from '@frontend/vanilla/core';
import { firstValueFrom } from 'rxjs';

import { MyBetsSlipEditBetHistoryViewModel } from '../../my-bets-base/models/my-bets-viewmodels';
import { MyBetsLoaderService } from './my-bets-loader.service';

@Injectable({ providedIn: 'root' })
export class MyBetsEditBetHistoryService {
    private static storageKey = 'EditHistoryRead';
    onEditBetsUpdate: EventEmitter<void> = new EventEmitter<void>();
    editBetHistoryBetslips: MyBetsSlipEditBetHistoryViewModel[] = [];
    private _hasEditBetHistoryBetslips = signal(false);
    private tooltipRead: boolean;

    constructor(
        private localStoreService: LocalStoreService,
        private myBetsLoader: MyBetsLoaderService,
        private sitecoreMyBets: SitecoreMyBetsConfig,
        private tokensConfig: CrmConfig,
    ) {
        this.tooltipRead = this.restoreTooltipRead();
    }

    get hasEditBetHistoryBetslips() {
        return this._hasEditBetHistoryBetslips();
    }

    async getEditBetBetslips(betslipId: string, associatedAccount: Nullable<string>): Promise<void> {
        await firstValueFrom(this.sitecoreMyBets.whenReady);

        const editBetResponse = await this.myBetsLoader.getEditBetBetslips(betslipId, associatedAccount);
        this.editBetHistoryBetslips = this.createEditBetsV4(editBetResponse);
        this._hasEditBetHistoryBetslips.set(editBetResponse.betslips.length > 0);
        this.onEditBetsUpdate.emit();
    }

    private createEditBetsV4(editBetResponse: MyBetsListSlimResponse): MyBetsSlipEditBetHistoryViewModel[] {
        const lastIndex = editBetResponse.betslips.length;
        const isOpen = lastIndex > 0 && editBetResponse.betslips[0].state === SlipState.Open;

        return editBetResponse.betslips.map((betslip, index) =>
            MyBetsSlipEditBetHistoryViewModel.createEditBetFromBetslipResponse(
                betslip,
                this.getEditBetsTitle(index, lastIndex, isOpen),
                this.tokensConfig,
            ),
        );
    }

    resetEditBetHistory(): void {
        this._hasEditBetHistoryBetslips.set(false);
        this.editBetHistoryBetslips = [];
    }

    private getEditBetsTitle(index: number, lastIndex: number, isOpen: boolean): string {
        switch (index) {
            case 0:
                return isOpen ? this.sitecoreMyBets.myBets.EditBetHistoryCurrentBet : this.sitecoreMyBets.myBets.EditBetHistoryFinalBet;
            case lastIndex - 1:
                return this.sitecoreMyBets.myBets.EditBetHistoryOriginalBet;
            default:
                return this.sitecoreMyBets.myBets.EditBetHistoryEdit.replace('{0}', (lastIndex - index - 1).toString());
        }
    }

    private restoreTooltipRead(): boolean {
        try {
            const storedValue = this.localStoreService.get(MyBetsEditBetHistoryService.storageKey);

            return typeof storedValue === typeof true;
        } catch {}

        return false;
    }

    getIsToolbarRead(): boolean {
        return this.tooltipRead;
    }

    saveTooltipRead(): void {
        this.tooltipRead = true;
        this.localStoreService.set(MyBetsEditBetHistoryService.storageKey, this.tooltipRead);
    }
}
